import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token in localStorage with robust error handling
    try {
      const storedToken = localStorage.getItem("liztv_token");
      const storedUser = localStorage.getItem("liztv_user");

      // Detect corrupted/invalid storage (e.g., literal "undefined" or "null" strings,
      // missing token while user exists). Clear and force re-login.
      const invalidToken = !storedToken || storedToken === "undefined" || storedToken === "null";
      const invalidUser = !storedUser || storedUser === "undefined" || storedUser === "null";

      if (invalidToken && !invalidUser) {
        // Mismatched state — clear to avoid stuck UI
        console.warn("[AuthContext] User found in localStorage but token is missing/invalid. Clearing for re-login.");
        localStorage.removeItem("liztv_token");
        localStorage.removeItem("liztv_user");
      } else if (!invalidToken && !invalidUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);
          // 부팅 시 서버에서 최신 사용자 정보로 동기화 (membership_type, needs_real_name 등 플래그 최신화)
          fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${storedToken}` } })
            .then((r) => (r.ok ? r.json() : null))
            .then((fresh) => {
              if (fresh) {
                setUser(fresh);
                localStorage.setItem("liztv_user", JSON.stringify(fresh));
              }
            })
            .catch(() => {});
        } catch (parseErr) {
          console.error("[AuthContext] Failed to parse stored user JSON. Clearing.", parseErr);
          localStorage.removeItem("liztv_token");
          localStorage.removeItem("liztv_user");
        }
      }
    } catch (e) {
      console.error("[AuthContext] Hydration error", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (accessToken, userData) => {
    if (!accessToken || !userData) {
      console.error("[AuthContext] login() called with missing token or user", { hasToken: !!accessToken, hasUser: !!userData });
      return;
    }
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem("liztv_token", accessToken);
    localStorage.setItem("liztv_user", JSON.stringify(userData));
    // 새 로그인 시 숨김 노출 안내 모달 dismiss 상태 초기화
    try { sessionStorage.removeItem("nc_restore_prompt_dismissed"); } catch (e) {}
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("liztv_token");
    localStorage.removeItem("liztv_user");
    localStorage.removeItem("nc_visited");
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("liztv_user", JSON.stringify(userData));
  };

  // 서버에서 최신 사용자 정보 가져오기 (멤버십 업그레이드 등 후 호출)
  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem("liztv_user", JSON.stringify(userData));
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  }, [token]);

  // 포인트 업데이트 함수
  const updatePoints = useCallback((newPoints) => {
    if (user) {
      const updatedUser = { ...user, points: newPoints };
      setUser(updatedUser);
      localStorage.setItem("liztv_user", JSON.stringify(updatedUser));
    }
  }, [user]);

  // 포인트 적립 함수
  const earnPoints = useCallback(async (reason) => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_URL}/api/points/earn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        const data = await response.json();
        updatePoints(data.new_balance);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error earning points:', error);
      return null;
    }
  }, [token, updatePoints]);

  // 포인트 사용 함수
  const spendPoints = useCallback(async (amount, description) => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_URL}/api/points/spend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount, description })
      });
      
      if (response.ok) {
        const data = await response.json();
        updatePoints(data.new_balance);
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error spending points:', error);
      return null;
    }
  }, [token, updatePoints]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    logout,
    updateUser,
    refreshUser,
    updatePoints,
    earnPoints,
    spendPoints,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
